import { type RefObject, useEffect, useRef, useState } from "react"

interface Size {
  width: number
  height: number
}

export function useTableScroll(scrollX: string | number = "max-content", paginationHeight: number = 56) {
  const tableWrapperRef = useRef<HTMLDivElement>(null)

  const size = useSize(tableWrapperRef)

  function getTableScrollY() {
    const height = size?.height

    if (!height) {
      return undefined
    }

    return height - paginationHeight
  }

  const scrollConfig = {
    x: scrollX,
    y: getTableScrollY(),
  }

  return {
    scrollConfig,
    tableWrapperRef,
  }
}

export function useSize<T extends HTMLElement = HTMLElement>(target?: RefObject<T | null>): Size | undefined {
  const [size, setSize] = useState<Size>()
  const observerRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    const el = target?.current
    if (!el) {
      return
    }

    observerRef.current = new ResizeObserver(([entry]) => {
      const { width, height } = entry?.contentRect ?? { width: 0, height: 0 }
      setSize({ width: width ?? 0, height: height ?? 0 })
    })
    observerRef.current.observe(el)

    return () => {
      observerRef.current?.disconnect()
    }
  }, [target])

  return size
}
